import { Component, Prop, Ref } from 'vue-property-decorator'
import ConfigurationSectionHeader from '@/modules/configurationModule/ConfigurationSectionHeader.vue'
import VtGrid from '@/components/VtGrid/VtGrid.vue'
import SynopticTemplatesModal from '@/modules/configurationModule/detail/templates/synoptic-templates/components/modal/configuration-templates-synoptic-templates-modal.vue'
import BaseCrudServerSideComponent from '@/shared/resources/base-crud-server-side.component'
import { BaseCrud } from '@/shared/models/cruds.models'
import ModalWrapper from '@/components/ModalWrapper.vue'
import { initialConfigurationTemplatesSynopticTemplatesGridState, ConfigurationTemplatesSynopticTemplatesGridState } from '@/modules/configurationModule/detail/templates/synoptic-templates/components/grid/configuration-templates-synoptic-templates-grid.model'
import { configurationSynopticTemplateService } from '@/modules/configurationModule/detail/templates/synoptic-templates/services/configuration-synoptic-template/configuration-synoptic-template.service'
import { EntityIdentifiersRequest, EntityIdentifierWithErrorResponse } from '@/shared/models/http/http.model'
import { GetContextMenuItemsParams, MenuItemDef } from '@ag-grid-enterprise/all-modules'
import { SYNOPTIC_TEMPLATES_UPDATE_CHANNEL } from '@/modules/configurationModule/detail/templates/shared/models/templates.models'
import {
    ConfigurationSynopticTemplateCreateRequest,
    ConfigurationSynopticTemplatePatchInBatchRequest,
    ConfigurationSynopticTemplateResponse
} from '@/modules/configurationModule/detail/templates/synoptic-templates/services/configuration-synoptic-template/configuration-synoptic-template.dto'

@Component({
    components: {
        ConfigurationSectionHeader,
        VtGrid,
        SynopticTemplatesModal
    }
})

export default class ConfigurationTemplatesSynopticTemplatesGrid extends BaseCrudServerSideComponent<ConfigurationSynopticTemplateResponse> implements BaseCrud {
    @Ref() readonly synopticTemplatesModalWrapper!: typeof ModalWrapper

    @Prop() readonly id!: number

    state:ConfigurationTemplatesSynopticTemplatesGridState

    get canUpdate(): boolean {
        return this.state.selectedRows.length === 1
    }

    get canDelete(): boolean {
        return !!this.state.selectedRows.length
    }

    get broadcastChannelPostMessageFunction(): Function {
        return () => this.state.broadcastChannel?.postMessage(SYNOPTIC_TEMPLATES_UPDATE_CHANNEL)
    }

    constructor() {
        super()
        this.state = initialConfigurationTemplatesSynopticTemplatesGridState()
    }

    /// HOOKS ///

    async mounted(): Promise<void> {
        if (this.id) {
            this.state.broadcastChannel = new BroadcastChannel(SYNOPTIC_TEMPLATES_UPDATE_CHANNEL)
            const synopticTemplateResponse = await this.getConfigurationTextTemplateById(this.id)
            this.showModal(this.$tc('configuration.actions.update'), synopticTemplateResponse, this.synopticTemplatesModalWrapper)
        }
    }

    beforeDestroy(): void {
        this.state.broadcastChannel?.close()
    }

    /// SERVICES CALLS ///

    private async _createSynopticTemplate(createRequest: ConfigurationSynopticTemplateCreateRequest): Promise<ConfigurationSynopticTemplateResponse | null> {
        const response = await this.$_toCallUnique(this.state.service.create(this.state.laboratoryId, createRequest))
        if (response) {
            this.$_showToastSuccess(this.$tc('configuration.toastMessages.created'))
        }
        return response
    }

    private async _patchSynopticTemplates(batchRequests: ConfigurationSynopticTemplatePatchInBatchRequest[]): Promise<EntityIdentifierWithErrorResponse[] | null> {
        if (batchRequests) {
            const response = await this.$_toCallUnique(this.state.service.patch(this.state.laboratoryId, batchRequests))
            if (response) {
                const elements = batchRequests.length - response.length
                if (elements > 0) {
                    const choice = elements > 1 ? 2 : 1
                    this.$_showToastSuccess(this.$tc('configuration.toastMessages.updated', choice, { elements }))
                }
                return response
            }
        }
        return null
    }

    private async _deleteSynopticTemplates(requestDelete: EntityIdentifiersRequest): Promise<EntityIdentifierWithErrorResponse[] | null> {
        const ids = requestDelete?.entityIdentifiers
        if (ids?.length) {
            const response = await this.$_toCallUnique(this.state.service.delete(this.state.laboratoryId, requestDelete))
            if (response) {
                const elements = ids.length - response.length
                if (elements > 0) {
                    const choice = elements > 1 ? 2 : 1
                    this.$_showToastSuccess(this.$tc('configuration.toastMessages.deleted', choice, { elements }))
                }
                return response
            }
        }
        return null
    }

    /// GRID OPERATIONS ///

    async activateDeactivateSelection(active: boolean, synopticTemplates: ConfigurationSynopticTemplateResponse[]): Promise<void> {
        if (synopticTemplates?.length) {
            const batchRequests = synopticTemplates.filter(synopticTemplate => synopticTemplate.active !== active)
                .map(synopticTemplate => ({
                    id: synopticTemplate.id,
                    active
                }))
            if (await this._patchSynopticTemplates(batchRequests)) {
                this.reloadData()
            }
        }
    }

    async createOperation(createRequest: ConfigurationSynopticTemplateCreateRequest): Promise<void> {
        if (createRequest) {
            const synopticTemplate = await this._createSynopticTemplate(createRequest)
            if (synopticTemplate) {
                this.reloadData()
                this.onUpdate(synopticTemplate)
            }
        }
    }

    async patchOperation(batchRequest: ConfigurationSynopticTemplatePatchInBatchRequest): Promise<void> {
        if (batchRequest) {
            if (await this._patchSynopticTemplates([batchRequest])) {
                this.reloadData()
            }
        }
    }

    private async _showModalAdviceWhenDelete(): Promise<boolean> {
        const labelConfirmation: string = this.$tc('configuration.grid.deleteConfirmation')
        const selectedRowsSize: number = this.state?.selectedRows?.length
        const labelGrid: string = this.$tc('configuration.grid.element', selectedRowsSize)
        const labelQuestion: string = this.$tc('configuration.grid.deleteQuestion')
        const labelAcceptAction: string = this.$tc('configuration.actions.delete')
        return this.$_showConfirmationModal(
            `${labelConfirmation} ${selectedRowsSize} ${labelGrid}, <br/> ${labelQuestion}`,
            labelAcceptAction
        )
    }

    async deleteOperation(requestDelete: EntityIdentifiersRequest): Promise<void> {
        if (requestDelete) {
            if (await this._showModalAdviceWhenDelete()) {
                if (await this._deleteSynopticTemplates(requestDelete)) {
                    this.reloadData()
                }
            }
        }
    }

    /// GRID EVENT HANDLERS ///

    onUpdate(synopticTemplate: ConfigurationSynopticTemplateResponse = this.state.gridApi?.getSelectedRows()[0]): void {
        if (synopticTemplate) {
            this.showModal(this.$tc('configuration.actions.update'), synopticTemplate, this.synopticTemplatesModalWrapper)
        }
    }

    async onDelete(): Promise<void> {
        if (this.state.selectedRows.length) {
            const requestDelete: EntityIdentifiersRequest = {
                entityIdentifiers: this.state.selectedRows.map(synopticTemplate => synopticTemplate.id)
            }
            await this.deleteOperation(requestDelete)
        }
    }

    onAdd(): void {
        this.showModal(this.$tc('configuration.actions.add'), null, this.synopticTemplatesModalWrapper)
    }

    async onActivate(): Promise<void> {
        await this.activateDeactivateSelection(true, this.state.selectedRows)
    }

    async onDeactivate(): Promise<void> {
        await this.activateDeactivateSelection(false, this.state.selectedRows)
    }

    getContextMenuItems(params: GetContextMenuItemsParams): MenuItemDef[] {
        return [
            {
                name: this.$tc('configuration.actions.update'),
                cssClasses: this.state.selectedRows.length > 1 ? ['not-show'] : [''],
                action: () => this.onUpdate(params?.node?.data)
            },
            {
                name: this.$tc('configuration.actions.delete'),
                cssClasses: this.state.selectedRows.length < 1 ? ['not-show'] : [''],
                action: this.onDelete
            },
            {
                name: params?.node?.data?.active ? this.$tc('configuration.actions.deactivate') : this.$tc('configuration.actions.activate'),
                cssClasses: this.state.selectedRows.length ? ['not-show'] : [''],
                action: () => this.activateDeactivateSelection(!params?.node?.data?.active, [params?.node?.data])
            },
            {
                name: this.$tc('configuration.actions.activate'),
                cssClasses: this.canActivateBatch ? [''] : ['not-show'],
                action: this.onActivate
            },
            {
                name: this.$tc('configuration.actions.deactivate'),
                cssClasses: this.canDeactivateBatch ? [''] : ['not-show'],
                action: this.onDeactivate
            }
        ]
    }

    async getConfigurationTextTemplateById(synopticTemplateId: number): Promise<ConfigurationSynopticTemplateResponse | null> {
        return this.$_toCallUnique(configurationSynopticTemplateService.get(this.state.laboratoryId, synopticTemplateId))
    }

    async onSynopticTemplatesModalUpdate(batchRequest: ConfigurationSynopticTemplatePatchInBatchRequest): Promise<void> {
        await this.patchOperation(batchRequest)
        this.broadcastChannelPostMessageFunction()
    }
}
